import { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import InfiniteScroll from "react-infinite-scroll-component";

// Components
import Message from "./Message";
import Notification from "./Notification";

// Calls
import { fetchChatMessagesRequest } from "../../api/calls/chat.calls";

// Styles
import styled from "styled-components";

const ChatBoxStyles = styled.div`
  height: 100%;
  width: 100%;
  overflow-y: auto;
  padding: 12px 18px 18px;
  display: flex;
  flex-direction: column-reverse;
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.primary[150]};
    border-radius: 999px;
  }

  .dateDivider {
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 16px 0 8px;
  }
  .dateDivider::before,
  .dateDivider::after {
    content: "";
    flex: 1;
    height: 1px;
    background: ${({ theme }) => theme.colors.semantic.separator};
  }
  .dateDivider span {
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.3px;
    text-transform: uppercase;
    color: ${({ theme }) => theme.colors.semantic.text.weak};
    padding: 2px 10px;
    background: ${({ theme }) => theme.colors.semantic.container.white};
    border-radius: 999px;
    border: 1px solid ${({ theme }) => theme.colors.semantic.separator};
  }
`;

const ChatBox = ({ serviceId }) => {
  const { user } = useSelector(state => state.app);
  const { newMessage } = useSelector(state => state.chatReducer);
  const [chatBoxState, setChatBoxState] = useState({
    messages: [],
    hasMore: true,
    lastMessageId: undefined
  });

  const { messages, lastMessageId } = chatBoxState;

  const scrollToBottom = () => {
    const chatBox = document.getElementById("scrollableDiv");
    if (chatBox) chatBox.scrollTop = chatBox.scrollHeight;
  };

  const getInitialMessages = useCallback(async () => {
    const response = await fetchChatMessagesRequest(serviceId);
    if (response.hasError) {
      toast.error(response.error.message);
      return;
    }

    setChatBoxState(prevState => ({
      ...prevState,
      messages: response.messages,
      hasMore: response.pagination.hasMore,
      lastMessageId: response.messages[response.messages.length - 1]?.id
    }));
    scrollToBottom();
  }, [serviceId]);

  useEffect(() => {
    getInitialMessages();
  }, [getInitialMessages]);

  const getAdditionalMessages = async () => {
    const response = await fetchChatMessagesRequest(serviceId, lastMessageId);
    if (response.hasError) {
      toast.error(response.error.message);
      return;
    }

    setChatBoxState(prevState => ({
      ...prevState,
      messages: [...response.messages, ...prevState.messages],
      hasMore: response.pagination.hasMore,
      lastMessageId: response.messages[response.messages.length - 1]?.id
    }));
  };

  useEffect(() => {
    if (newMessage) {
      setChatBoxState(prevState => ({
        ...prevState,
        messages: [...prevState.messages, newMessage]
      }));
    }
  }, [newMessage]);

  // Group by date, then within each date collapse consecutive same-sender
  // text messages into a bubble group. System messages always stand alone.
  const buildGroupedByDate = (allMessages = messages) => {
    const sorted = [...allMessages].sort(
      (m1, m2) => m1.createdAt - m2.createdAt
    );
    const uniqueDates = [...new Set(sorted.map(m => m.date))];

    return uniqueDates.map(date => {
      const dayMessages = sorted.filter(m => m.date === date);
      const groups = [];
      let last = null;
      dayMessages.forEach(m => {
        if (
          last &&
          last.type === m.type &&
          m.type === "text" &&
          last.senderId === m.sender?.id
        ) {
          last.items.push(m);
        } else {
          last = {
            type: m.type,
            senderId: m.sender?.id,
            items: [m]
          };
          groups.push(last);
        }
      });
      return { date, groups };
    });
  };

  const grouped = buildGroupedByDate();

  return (
    <ChatBoxStyles id="scrollableDiv">
      <InfiniteScroll
        dataLength={messages.length}
        hasMore={true}
        next={getAdditionalMessages}
        scrollThreshold={0.5}
        inverse={true}
        scrollableTarget="scrollableDiv"
      >
        {grouped.map((day, i) => (
          <div key={`day-${i}`}>
            <div className="dateDivider">
              <span>{day.date}</span>
            </div>
            {day.groups.map((group, gi) => {
              if (group.type === "system") {
                const m = group.items[0];
                return (
                  <Notification
                    key={m.id}
                    text={m.body}
                    time={m.time}
                  />
                );
              }
              const isMine = String(group.senderId) === String(user.businessId);
              return group.items.map((m, idx) => {
                const isFirst = idx === 0;
                const isLast = idx === group.items.length - 1;
                return (
                  <Message
                    key={m.id}
                    isMyMessage={isMine}
                    text={m.body}
                    time={m.time}
                    isFirstInGroup={isFirst}
                    isLastInGroup={isLast}
                  />
                );
              });
            })}
          </div>
        ))}
      </InfiniteScroll>
    </ChatBoxStyles>
  );
};

export default ChatBox;
